import React, { useEffect, Suspense, lazy } from "react";

import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { SiteLayout } from "@/components/SiteLayout";

// Lazy load pages for better initial load performance
const Home = lazy(() => import("@/pages/Home"));
const FocusAreas = lazy(() => import("@/pages/FocusAreas"));
const Collaborate = lazy(() => import("@/pages/Collaborate"));
const CellPreview = lazy(() => import("@/pages/CellPreview"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const NotFound = () => (
  <div className="mx-auto max-w-6xl px-5 py-20 md:px-8">
    <div className="rounded-sm border border-white/10 bg-white/5 p-8 backdrop-blur-[18px]">
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-white">Page not found</h1>
      <p className="mt-3 text-sm text-white/70">Return to Home.</p>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    // Remove platform-injected badge (requested)
    const removeBadge = () => {
      const el = document.getElementById("emergent-badge");
      if (el) el.remove();
    };

    removeBadge();

    // Keep removing if it gets injected after load
    const observer = new MutationObserver(() => removeBadge());
    observer.observe(document.documentElement, { childList: true, subtree: true });

    const t = window.setTimeout(removeBadge, 1200);
    return () => {
      observer.disconnect();
      window.clearTimeout(t);
    };
  }, []);


  return (
    <div className="dark">
      <BrowserRouter>
        <ScrollToTop />
        <SiteLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/focus-areas" element={<FocusAreas />} />
              <Route path="/collaborate" element={<Collaborate />} />
              <Route path="/cell-preview" element={<CellPreview />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/why" element={<Navigate to="/focus-areas" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </SiteLayout>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
